import React, { useState } from 'react';
import { ToggleLeft, ToggleRight, RefreshCw, AlertTriangle, ChevronDown, ChevronRight, ShieldAlert } from 'lucide-react';
import ClinicalFlagConfirmModal from './ClinicalFlagConfirmModal';

/**
 * FeatureFlagsCard — server-controlled runtime toggles list. Stateless on
 * data; manages its own collapsed/expanded UI state. Defaults collapsed to
 * keep the Admin page scroll length manageable as the flag list grows.
 *
 * Extracted from Admin.jsx; made collapsible.
 */
const FeatureFlagsCard = ({ featureFlags, flagBusy, flagError, onToggle }) => {
  const [open, setOpen] = useState(false);
  // Flag id pending clinical-signoff confirmation (only set when turning ON
  // a `requiresClinicalSignoff` flag). Cleared on cancel/confirm.
  const [pendingClinical, setPendingClinical] = useState(null);
  const totalCount = Object.keys(featureFlags || {}).length;
  const onCount = Object.values(featureFlags || {}).filter((f) => f.enabled).length;

  // Enabling a clinically-gated flag routes through the confirm modal;
  // everything else (including turning any flag OFF) toggles immediately.
  const requestToggle = (flagId, flag) => {
    const turningOn = !flag.enabled;
    if (turningOn && flag.requiresClinicalSignoff) {
      setPendingClinical(flagId);
      return;
    }
    onToggle(flagId, turningOn);
  };

  return (
    <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl mb-10" data-testid="admin-feature-flags-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between p-6 hover:bg-enf-surface-mid/50 transition-colors rounded-xl"
        aria-expanded={open}
        data-testid="admin-feature-flags-toggle"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown className="w-4 h-4 text-enf-text-muted" /> : <ChevronRight className="w-4 h-4 text-enf-text-muted" />}
          <ToggleRight className="w-4 h-4 text-enf-role-info" />
          <div className="text-sm font-semibold uppercase tracking-widest text-enf-text-secondary">Feature flags</div>
        </div>
        <span className="text-xs text-enf-text-muted" data-testid="admin-feature-flags-count">
          {onCount} of {totalCount} ON
        </span>
      </button>

      {open && (
        <div className="px-6 pb-6">
          <p className="text-xs text-enf-text-muted leading-relaxed mb-5">
            Server-controlled runtime toggles. Flip a feature on or off for every user instantly — no app-store redeploy needed.
            Defaults are <span className="text-enf-text-secondary font-semibold">OFF</span> until you light them up.
          </p>

          {flagError && (
            <div className="mb-3 px-3 py-2 bg-enf-accent/10 border border-enf-accent-fg/30 text-enf-accent-fg text-xs rounded-md flex items-center gap-2">
              <AlertTriangle className="w-3 h-3" /> {flagError}
            </div>
          )}

          <div className="space-y-2">
            {Object.entries(featureFlags || {}).map(([flagId, flag]) => (
              <div
                key={flagId}
                className={`flex items-start gap-3 p-3 rounded-md border transition-colors ${
                  flag.enabled
                    ? 'bg-adaptive-sage/5 border-adaptive-sage/30'
                    : 'bg-enf-surface-mid border-enf-surface-border'
                }`}
                data-testid={`feature-flag-row-${flagId}`}
              >
                <button
                  type="button"
                  onClick={() => requestToggle(flagId, flag)}
                  disabled={flagBusy === flagId}
                  className="flex-shrink-0 mt-0.5 disabled:opacity-50"
                  aria-label={`Toggle ${flag.label}`}
                  data-testid={`feature-flag-toggle-${flagId}`}
                >
                  {flag.enabled ? (
                    <ToggleRight className="w-9 h-9 text-adaptive-sage" />
                  ) : (
                    <ToggleLeft className="w-9 h-9 text-enf-text-muted" />
                  )}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-enf-text-primary">{flag.label}</span>
                    <span className="text-[10px] uppercase tracking-widest font-mono text-enf-text-muted">{flagId}</span>
                    {flag.enabled && (
                      <span className="text-[10px] uppercase tracking-widest font-semibold text-adaptive-sage bg-adaptive-sage/15 border border-adaptive-sage/30 px-1.5 py-0.5 rounded">ON</span>
                    )}
                    {flag.requiresClinicalSignoff && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold text-enf-role-warning bg-enf-role-warning/10 border border-enf-role-warning/30 px-1.5 py-0.5 rounded"
                        title="Requires documented clinical sign-off + liability insurance before enabling (Rule 8a)"
                      >
                        <ShieldAlert className="w-3 h-3" /> Sign-off
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-enf-text-muted mt-1 leading-relaxed">{flag.description}</p>
                </div>
                {flagBusy === flagId && (
                  <RefreshCw className="w-3.5 h-3.5 text-enf-text-muted animate-spin flex-shrink-0 mt-2" />
                )}
              </div>
            ))}
            {totalCount === 0 && (
              <div className="text-sm text-enf-text-muted py-6 text-center">
                Flags loading…
              </div>
            )}
          </div>
        </div>
      )}

      <ClinicalFlagConfirmModal
        flag={pendingClinical ? featureFlags?.[pendingClinical] : null}
        busy={flagBusy === pendingClinical}
        onCancel={() => setPendingClinical(null)}
        onConfirm={() => {
          const id = pendingClinical;
          setPendingClinical(null);
          if (id) onToggle(id, true);
        }}
      />
    </div>
  );
};

export default FeatureFlagsCard;
